import { useCallback, useEffect, useRef, useState } from 'react';

interface UnsavedChangesGuardOptions {
  dirty: boolean;
  /** Called by the host when the user confirms "Discard". */
  onDiscard: () => void;
}

/**
 * Tracks dirty state on a form and intercepts close/cancel actions with a
 * confirm modal. Returns the modal-open boolean + helpers to:
 * - `requestClose()` — called by the host when the user attempts to close
 *   (X button, ESC, backdrop click). If dirty, shows the modal; otherwise
 *   immediately invokes the host's onDiscard.
 * - `confirmDiscard()` — modal "Discard changes" button.
 * - `cancelDiscard()` — modal "Keep editing" button (closes the modal,
 *   keeps the dirty state intact).
 *
 * Also wires the browser-native `beforeunload` event so the user can't
 * navigate away or refresh with unsaved changes without a warning. The
 * native warning text is browser-controlled, but the listener prevents the
 * silent loss of data.
 */
export const useUnsavedChangesGuard = ({ dirty, onDiscard }: UnsavedChangesGuardOptions) => {
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const onDiscardRef = useRef(onDiscard);
  onDiscardRef.current = onDiscard;

  useEffect(() => {
    if (!dirty) return undefined;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [dirty]);

  const requestClose = useCallback(() => {
    if (dirty) {
      setIsModalOpen(true);
      return false;
    }
    onDiscardRef.current();
    return true;
  }, [dirty]);

  const confirmDiscard = useCallback(() => {
    setIsModalOpen(false);
    onDiscardRef.current();
  }, []);

  const cancelDiscard = useCallback(() => setIsModalOpen(false), []);

  return { isModalOpen, requestClose, confirmDiscard, cancelDiscard };
};
